import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TreeSelectModule } from 'primeng/treeselect';
import { MenuItem, MessageService, TreeNode } from 'primeng/api';
import { Popover, PopoverModule } from 'primeng/popover';
import { AuthService } from '@core/auth/services/auth-service';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { BusinessGroupedModel } from '@features/business/models/business.model';
import { HttpErrorResponse } from '@angular/common/http';
import { BusinessLogo } from '@shared/components/business-logo/business-logo';
import { BusinessStateService } from '@core/services/business-state.service';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { UserApi } from '@features/user/services/user-api';
import { UserDetailModel } from '@features/user/models/user.model';

interface Node {
  Name: string,
  Id: string,
  Kind: string,
  LogoUrl: string
}import { BusinessSelection } from '@core/layout/models/business-selection.model';
import { UnsavedChangesService } from '@features/forms/services/unsaved-changes.service';


type SelectionKind = 'All' | 'Group' | 'Business' | 'Office';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    TreeSelectModule,
    FormsModule,
    ButtonModule,
    PopoverModule,
    BusinessLogo
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  providers: [MessageService]
})

export class Header implements OnInit {
  @Output() toggleSidebar = new EventEmitter<void>();
  private messageService = inject(MessageService);
  private authService = inject(AuthService);
  private businessApi = inject(BusinessApi);
  private userApi = inject(UserApi);
  private readonly businessStateService = inject(BusinessStateService);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  userNameInitial: string = '';
  userMenuItems: MenuItem[] = [];

  selectedBusinessGroup: TreeNode | null = null;
  businessGroupTree: TreeNode[] = [];
  initialEmailCharacter: string = '';
  userData: UserDetailModel | null = null;

  ngOnInit(): void {
    this.getBusinessGroupList();
    this.userData = this.authService.getUser();
    this.initialEmailCharacter = `${this.userData?.Email.charAt(0) || ''}`.toUpperCase();
    localStorage.removeItem('businessSelection');
    
    // Suscribirse a eventos de refresco del listado
    this.businessStateService.refreshBusinessList$.subscribe(() => {
      this.getBusinessGroupList();
    });
  }

  get userFullName(): string {
    const name = this.userData?.Name?.trim() ?? '';
    const lastName = this.userData?.LastName?.trim() ?? '';
    return `${name} ${lastName}`.trim();
  }

  getBusinessGroupList() {
    this.businessApi.getBusinessGrouped().subscribe({
      next: (response: DataEndpoint<BusinessGroupedModel[]>) => {
        if (response.Status === 200) {
          const businessGroups = response.Data ?? [];
          this.businessGroupTree = this.createBusinessGroupTree(businessGroups);
          this.findSelectedNode();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message
        });
      }
    })
  }

  private findSelectedNode(): void {
    const persisted = this.businessStateService.getSelectedBusiness();

    const persistedKind = persisted?.data?.kind;
    const resolvedNode =
      persisted?.data?.id && persistedKind
        ? this.findBusinessNodeById(persisted.data.id, persistedKind)
        : null;

    this.selectedBusinessGroup = resolvedNode ?? this.businessGroupTree[0]; // Setear por defecto "Todos"

    // Si había selección persistida pero no se pudo restaurar, limpiarla
    if (persisted && !resolvedNode) {
      this.businessStateService.clearPersistedSelection();
    }

    // Bloquear deselección del nodo actual
    if (this.selectedBusinessGroup) {
      this.selectedBusinessGroup.selectable = false;
    }

    // Notificar selección final
    this.businessStateService.setSelectedBusiness(this.selectedBusinessGroup);
  }

  private createBusinessGroupTree(businessGroups: BusinessGroupedModel[]): TreeNode[] {
    const tree: TreeNode[] = [];

    // Crear el nodo "Todos" con logo de Mesgo
    const allNode: TreeNode = {
      key: 'all-all',
      label: 'Todos',
      data: {
        id: 'all',
        kind: 'all',
        name: 'Todos',
        logoUrl: 'assets/icons/mesgo-logo.png'
      },
      children: [],
      expanded: true // Expandir por defecto para mostrar los grupos
    };

    // Agregar todos los grupos empresariales como hijos de "Todos"
    for (const group of businessGroups) {
      const groupNode: TreeNode = {
        key: `${group.Kind}-${group.Id}`,
        label: group.Name,
        data: {
          id: group.Id,
          kind: group.Kind,
          logoUrl: group.LogoUrl,
          name: group.Name,
          group: group
        },
        children: [],
        expanded: false
      };

      // Crear nodos para cada empresa del grupo
      if (group.Business && group.Business.length > 0) {
        for (const business of group.Business) {
          const businessNode: TreeNode = {
            key: `${business.Kind}-${business.Id}`,
            label: business.Name,
            data: {
              id: business.Id,
              kind: business.Kind,
              logoUrl: business.LogoUrl,
              name: business.Name,
              business: business
            },
            children: [],
            expanded: false
          };

          // Crear nodos para cada sede de la empresa
          if (business.Offices && business.Offices.length > 0) {
            for (const office of business.Offices) {
              const officeNode: TreeNode = {
                key: `${office.Kind}-${office.Id}`,
                label: office.Name,
                data: {
                  id: office.Id,
                  kind: office.Kind,
                  logoUrl: business.LogoUrl,
                  name: office.Name,
                  office: office
                },
                children: []
              };
              businessNode.children!.push(officeNode);
            }
          }

          groupNode.children!.push(businessNode);
        }
      }

      allNode.children!.push(groupNode);
    }

    tree.push(allNode);
    return tree;
  }

  setNodeData(data: Node): TreeNode  {
    const node: TreeNode = {
      key: `${data.Kind}-${data.Id}`,
      label: data.Name,
      data: {
        id: data.Id,
        kind: data.Kind,
        name: data.Name,
        logoUrl: data.LogoUrl
      },
      children: [],
      expanded: data.Kind === 'all' // Expandir por defecto para mostrar los grupos
    };

    if (data.Kind !== 'all') {
      node.data[data.Kind] = data;
    }
    return node
  }

  /**
   * Busca un nodo en el árbol de empresas por ID y tipo
   * @param id El ID del nodo a buscar
   * @param kind El tipo del nodo ('all', 'group', 'business', 'office')
   * @returns El TreeNode encontrado o null si no existe
   */
  private findBusinessNodeById(id: string, kind: string): TreeNode | null {
    const targetKind = kind.toLowerCase();
    // Función recursiva para buscar en el árbol
    const searchTree = (nodes: TreeNode[]): TreeNode | null => {
      for (const node of nodes) {
        const nodeKind = node.data?.kind?.toString().toLowerCase();
        if (node.data?.id === id && nodeKind === targetKind) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = searchTree(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    return searchTree(this.businessGroupTree);
  }

  private initializeUserMenu(): void {
    this.userMenuItems = [
      {
        label: 'Cerrar sesión',
        command: () => this.logout()
      }
    ];
  }

  logout() {
    this.authService.logout();
  }

  async onBusinessSelect(event: TreeNodeSelectEvent): Promise<void> {
    const canChange = await this.unsavedChangesService.canDeactivateAsync();
    if (!canChange) {
      setTimeout(() => {
        this.selectedBusinessGroup = this.businessStateService.getSelectedBusiness();
      });
      return;
    }

    // Buscar el nodo original, ya que PrimeNG devuelve un clon cuando se filtra
    const originalNode = this.findBusinessNodeById(event.node.data?.id, event.node.data?.kind) || event.node;

    // Habilitar recursivamente todos los nodos del árbol
    this.enableAllNodesRecursively(this.businessGroupTree);

    // Prevenir que el ítem seleccionado se pueda deseleccionar
    originalNode.selectable = false;
    this.selectedBusinessGroup = originalNode;

    // Almacenar en el estado general
    this.businessStateService.setSelectedBusiness(originalNode);
  }

  /**
   * Habilita recursivamente todos los nodos del árbol
   */
  private enableAllNodesRecursively(nodes: TreeNode[]): void {
    for (const node of nodes) {
      node.selectable = true;
      if (node.children && node.children.length > 0) {
        this.enableAllNodesRecursively(node.children);
      }
    }
  }

  onBusinessSelected(node: TreeNode | null) {
    if (!node?.data) return;

    // Asegurarse de usar el nodo original para evitar problemas de referencia
    const originalNode = this.findBusinessNodeById(node.data.id, node.data.kind) || node;
    this.selectedBusinessGroup = originalNode;

    const kind = originalNode.data.kind as SelectionKind;

    const selection: BusinessSelection = {
      id: originalNode.data.id,
      kind: originalNode.data.kind,
      name: originalNode.data.name,
      logoUrl: originalNode.data.logoUrl
    };

    localStorage.setItem('businessSelection', JSON.stringify(selection));

    window.dispatchEvent(new CustomEvent('businessSelectionChanged', { detail: selection }));
  }
}
